<script setup lang="ts">
import {ref, watch} from "vue";
import {Disclosure, DisclosureButton, DisclosurePanel} from '@headlessui/vue'
import {ChevronUpIcon} from '@heroicons/vue/20/solid'
import adminAPI from "@/services/api";
import {CalendarDaysIcon} from '@heroicons/vue/24/outline';

import {useNotificationStore} from "@/store";
import {format, parse} from "date-fns";
import {fr} from "date-fns/locale";
import {RadioGroup, RadioGroupLabel, RadioGroupOption} from '@headlessui/vue'
import {ListCompetentCourtsForClaimResponse, RetrieveClaimWithRelationsResponse} from "@upclaim/sdk/src/data-contracts";

// Refs
const hearingsByJuridictions = ref<Object>({})
const isLoading = ref<boolean>(false);
const datePickerHearingDate = ref<Date>()
const datePickerOpen = ref<boolean>(false)
const selectedDateHearing = ref<Date>()


// Props
const props = defineProps<{
  claim: RetrieveClaimWithRelationsResponse,
  jurisdictions: Omit<ListCompetentCourtsForClaimResponse, "all">
}>();

const emits = defineEmits(["update-claim"]);

// Data
const minHearingDate = new Date(new Date().setDate(new Date().getDate())).toISOString().split('T')[0]
datePickerHearingDate.value = new Date(minHearingDate)


fetchHearings()

function extractDatesFromJuridiction(juridicationName) {
  if (!hearingsByJuridictions.value[juridicationName]) {
    return []
  }

  // Filter key with "2023-12-01" regex match
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return Object.keys(hearingsByJuridictions.value[juridicationName])
      .filter(key => dateRegex.test(key));
}


async function fetchHearings() {
  isLoading.value = true
  const hearings = await adminAPI.listHearings({procedure: 'ongoing', groupBy: 'juridiction'});
  isLoading.value = false
  if (hearings) {
    hearingsByJuridictions.value = hearings;
  }
}


// Return an array of dates for a juridiction
function isDateInClaimPayload(date, jurisdictionName) {
  if (hearingsByJuridictions.value.hasOwnProperty(jurisdictionName)) {
    const jurisdictionDates = hearingsByJuridictions.value[jurisdictionName];
    if (jurisdictionDates.hasOwnProperty(date)) {
      const hearings = jurisdictionDates[date];
      for (const hearing of hearings) {
        if (hearing.procedure.claimId === props.claim.id) {
          return true;
        }
      }
    }
  }
  // If false is returned we'll display a date picker
  return false;
}

function selectHearingDate(date) {
  selectedDateHearing.value = date
  datePickerHearingDate.value = undefined
}

function removeHearingDate() {
  selectedDateHearing.value = undefined
}

async function setHearing(hearingDate: Date, juridictionId: number) {
  isLoading.value = true
  const { success } = await adminAPI.upsertNextHearing(props.claim.id, {
    juridictionId,
    hearingDate,
    // TODO: Dynamic values
    hearingTypeId: 1,
    lawyerId: 1,
    procedureType: "PROCEDURE_CLASSIQUE",
  });
  if (success) {
    await fetchHearings()
    emits('update-claim')
    const notification = 'L\'audience à été crée'
    useNotificationStore().emitNotification({title: notification, type: 'success'});
  }
  isLoading.value = false;
}

</script>
<template>
  <div class="bg-white rounded-lg shadow" v-if="!isLoading">
    <div class="p-6 pt-0">
      <Disclosure v-slot="{ open }" v-for="juridiction in jurisdictions">
        <DisclosureButton
            class="flex justify-between w-full px-4 py-2 my-3 text-sm font-medium text-left text-purple-900 bg-purple-100 rounded-lg hover:bg-purple-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75"
        >
          <span>{{ juridiction.name }} : {{
              extractDatesFromJuridiction(juridiction.name).length
            }} dates d'audiences prévues</span>
          <ChevronUpIcon
              :class="open ? 'rotate-180 transform' : ''"
              class="w-5 h-5 text-blue-500"
          />
        </DisclosureButton>
        <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">
          <a v-if="juridiction?.code"
             target="_blank"
             :href="`https://www.infogreffe.fr/fiche-greffe/${juridiction.code}`"
             class="inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
            <CalendarDaysIcon class="w-5 h-5 mr-1"/>
            Calendrier des audiences
          </a>
          <RadioGroup class="my-4" v-if="extractDatesFromJuridiction(juridiction.name).length">
            <div class="grid grid-cols-3 gap-3">
              <RadioGroupOption as="template" v-for="date in extractDatesFromJuridiction(juridiction.name)" :key="date"
                                v-slot="{ active, checked }"
                                :disabled="isDateInClaimPayload(date, juridiction.name)"
                                @click="!isDateInClaimPayload(date, juridiction.name) ? selectHearingDate(date) : null"
              >
                <div
                    :class="[isDateInClaimPayload(date, juridiction.name)  ? 'bg-purple-100 cursor-auto' : 'border border-grey-600 cursor-pointer', active ? 'bg-purple-100 ring-2 ring-purple-600' : '',  'flex items-center justify-center rounded-md py-3 px-3 text-sm font-semibold capitalize sm:flex-1']">
                  <RadioGroupLabel as="span">
                    <div>{{
                        format(parse(date, 'yyyy-MM-dd', new Date()), "eeee d MMMM yyyy", {locale: fr})
                      }}
                    </div>
                    <span class="text-black capitalize">{{
                        hearingsByJuridictions[juridiction.name][date].length
                      }}{{
                        hearingsByJuridictions[juridiction.name][date].length > 1 ? " audiences" : " audience"
                      }}
                    </span>


                  </RadioGroupLabel>
                </div>
              </RadioGroupOption>
            </div>
          </RadioGroup>
          <div class="flex flex-col items-center justify-center ">
            <div v-if="!extractDatesFromJuridiction(juridiction.name).length" class="my-2">Aucune audience prévue à ce
              tribunal
            </div>
            <div
                v-if="extractDatesFromJuridiction(juridiction.name).length && !datePickerOpen && !selectedDateHearing"
                class="inline-flex items-center px-3 py-2 my-2 text-sm font-semibold text-gray-900 bg-white rounded-md shadow-sm cursor-pointer w-fit ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                @click="datePickerOpen = true">
              Choisir une autre date
            </div>
            <VDatePicker v-if="!extractDatesFromJuridiction(juridiction.name).length || datePickerOpen"
                         v-model="datePickerHearingDate"
                         :minDate="minHearingDate"
                         mode="date"/>
            <div
                v-if="datePickerOpen || !extractDatesFromJuridiction(juridiction.name).length && datePickerHearingDate"
                class="inline-flex items-center block px-3 py-2 my-2 text-sm font-semibold text-gray-900 bg-white rounded-md shadow-sm cursor-pointer w-fit ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                @click="datePickerHearingDate ? setHearing(datePickerHearingDate, juridiction.id) : null">
              Sauvegarder l'audience
            </div>
            <div
                v-else-if="selectedDateHearing"
                class="inline-flex items-center block px-3 py-2 my-2 text-sm font-semibold text-gray-900 bg-white rounded-md shadow-sm cursor-pointer w-fit ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                @click="selectedDateHearing ? setHearing(selectedDateHearing, juridiction.id) : null">
              Sauvegarder l'audience
            </div>

          </div>
        </DisclosurePanel>
      </Disclosure>
    </div>
  </div>
</template>
